import { Row } from '@tanstack/react-table'

import Title from '../../Cells/Title'
import DueDateComponent from '../../Cells/DueDateComponent'
import Status from '../../Cells/Status'
import Type from '../../Cells/Type'
import UpdatedAt from '../../Cells/UpdatedAt'
import UsersList from '../../Cells/UsersList'
import Priority from '../../Cells/Priority'
import Category from '../../Cells/Category'
import SingleUser from '../../Cells/SingleUser'
import TagCell from '@SonarCX/components/molecules/Table/Cells/Tag'
import DateComponent from '@SonarCX/components/molecules/Table/Cells/Date'
import CustomField from '@SonarCX/components/molecules/Table/Cells/CustomFieldComponent'

import {
  CATEGORY_TAB,
  COLUMN_KEYS
} from '@SonarCX/providers/TaskPageStateProvider'
import { isUUID } from '@SonarCX/utilities'

type GetCellType = (opts: {
  index: number
  order: string[]
  tab: number
  observerId: string
  taskCfs: LocalCustomField[]
}) => (() => any) | ((props: any) => JSX.Element)

const CustomFieldCell = (props, taskCfs, type, index, order) => {
  const field = taskCfs.find(cf => cf.id === type)
  const { task } = props
  const mockRow = {
    original: {
      ...task,
      // meta from tables comes as val, meta from api comes as value.
      meta: task.meta
        ? task.meta.map(x => ({ ...x, val: x.val ?? x.value }))
        : []
    }
  } as Row<WithMeta<unknown>>

  if (!isUUID(type) || !field) {
    console.error(`Invalid type`, { type, index, order })
    return null
  }

  return <CustomField row={mockRow} customField={field} />
}

export const getCell: GetCellType = ({
  index,
  order,
  tab,
  observerId,
  taskCfs
}) => {
  const nullComp = () => null

  if (index > order.length) {
    console.error(`Cell out of bounds`, { index, order })
    return nullComp
  }

  const type = order[index]

  switch (type) {
    case COLUMN_KEYS.title:
      return props => <Title lighter {...props} />
    case COLUMN_KEYS.due_date:
      return DueDateComponent
    case COLUMN_KEYS.status:
      return Status
    case COLUMN_KEYS.tags:
      return TagCell
    case COLUMN_KEYS.type:
      return Type
    case COLUMN_KEYS.updated_at:
      return UpdatedAt
    case COLUMN_KEYS.assignee_ids:
      return ({ task }) => {
        const assignees = [...(task?.assignee_ids ?? [])]
        return (
          <UsersList
            prop='assignee_ids'
            taskID={task.id}
            list={[...new Set(assignees)]}
            observerId={observerId}
          />
        )
      }
    case COLUMN_KEYS.watcher_ids:
      return ({ task }) => {
        const watchers = [...(task?.watcher_ids ?? [])]
        return (
          <UsersList
            prop='watcher_ids'
            taskID={task.id}
            list={[...new Set(watchers)]}
            observerId={observerId}
          />
        )
      }
    case COLUMN_KEYS.priority:
      return ({ task }) => <Priority task={task} />
    case COLUMN_KEYS.category_id:
      if (tab === CATEGORY_TAB) {
        return null
      }
      return ({ task }) => <Category task={task} />
    case COLUMN_KEYS.concerning_user_id:
      return ({ task }) => (
        <SingleUser
          taskID={task.id}
          field='concerning_user_id'
          external
          observerId={observerId}
        />
      )
    case COLUMN_KEYS.created_by:
      return ({ task }) => (
        <SingleUser
          taskID={task.id}
          field='created_by'
          type='Author'
          disableEdit
          observerId={observerId}
        />
      )
    case COLUMN_KEYS.owner_id:
      return ({ task }) => (
        <SingleUser
          taskID={task.id}
          field='owner_id'
          type='Owner'
          observerId={observerId}
        />
      )
    case COLUMN_KEYS.primary_assignee_id:
      return ({ task }) => (
        <SingleUser
          taskID={task.id}
          field='primary_assignee_id'
          type='Task Lead'
          observerId={observerId}
        />
      )
    case COLUMN_KEYS.created_at:
      return ({ task }) => <DateComponent value={task.created_at} />
    default:
      return props => CustomFieldCell(props, taskCfs, type, index, order)
  }
}
